/**
 * As contas do recorte de imagem (sem tela, para dar para testar):
 * - foto: um quadrado da imagem, com zoom e arrastando o centro;
 * - logo: o tamanho que cabe numa caixa, sem distorcer e sem aumentar.
 *
 * Lê: nada.
 * Usado por: componentes/EscolherImagem.tsx.
 */

export interface Recorte {
  x: number;
  y: number;
  lado: number;
}

export interface Ponto {
  x: number;
  y: number;
}

const limitar = (valor: number, minimo: number, maximo: number) => Math.min(Math.max(valor, minimo), Math.max(minimo, maximo));

/** O quadrado da imagem que vira a foto: zoom 1 = o maior quadrado possível; centro em pixels da imagem. */
export function recorteQuadrado(largura: number, altura: number, zoom: number, centro: Ponto): Recorte {
  const lado = Math.min(largura, altura) / Math.max(1, zoom);
  return {
    x: limitar(centro.x - lado / 2, 0, largura - lado),
    y: limitar(centro.y - lado / 2, 0, altura - lado),
    lado,
  };
}

/** Centro inicial: no meio; numa foto em pé, um pouco acima do meio (onde costuma estar o rosto). */
export function centroInicial(largura: number, altura: number): Ponto {
  return { x: largura / 2, y: altura > largura ? altura * 0.4 : altura / 2 };
}

/**
 * Arrastar a prévia (dx, dy em pixels da tela) move a imagem junto com o dedo:
 * o centro do recorte vai para o lado contrário. Nunca sai da imagem.
 */
export function moverCentro(largura: number, altura: number, zoom: number, centro: Ponto, dx: number, dy: number, ladoDaPrevia: number): Ponto {
  const atual = recorteQuadrado(largura, altura, zoom, centro);
  const escala = atual.lado / ladoDaPrevia;
  const meio = atual.lado / 2;
  return {
    x: limitar(atual.x + meio - dx * escala, meio, largura - meio),
    y: limitar(atual.y + meio - dy * escala, meio, altura - meio),
  };
}

/** Tamanho que cabe na caixa (ex.: logo em 800 × 240) sem distorcer; imagem pequena fica como está. */
export function tamanhoQueCabe(largura: number, altura: number, maximoLargura: number, maximoAltura: number): { largura: number; altura: number } {
  const escala = Math.min(1, maximoLargura / largura, maximoAltura / altura);
  return { largura: Math.max(1, Math.round(largura * escala)), altura: Math.max(1, Math.round(altura * escala)) };
}
